import React, { useState } from 'react';
import { BookmarkSimple, MagnifyingGlass, SealCheck, Wallet, MapPin, Trash, Archive } from '@phosphor-icons/react';
import { Button } from '../components/ui/Button';
import { PAGE_ROUTE } from '../lib/routes';
import { getSession, getBookmarks, removeBookmark, BookmarkedProfile, formatBudget, cn } from '../lib/utils';
interface DashboardProfileSalvateProps {
  navigate: (page: string, context?: any) => void;
}
export function DashboardProfileSalvate({
  navigate
}: DashboardProfileSalvateProps) {
  const session = getSession();
  const isSupply = session?.role === 'supply';
  const [bookmarks, setBookmarks] = useState<BookmarkedProfile[]>(() => getBookmarks());
  // Wrong-role guard
  if (!isSupply) {
    return <div className="space-y-6" data-id="element-3400">
        <h1 className="text-2xl font-bold text-light-1" data-id="element-3401">Anunțuri salvate</h1>
        <div className="rounded-2xl bg-dark-2 border border-dark-3 p-8 text-center" data-id="element-3402">
          <p className="text-sm text-light-3 leading-relaxed max-w-md mx-auto" data-id="element-3403">
            Această secțiune este disponibilă pentru conturile de proprietar sau
            agent.
          </p>
          <Button onClick={() => navigate(PAGE_ROUTE.DASHBOARD_PROFIL)} className="mt-5 bg-dark-3 hover:bg-dark-4 text-light-1 border border-dark-4" data-id="element-3404">
            Înapoi la profil
          </Button>
        </div>
      </div>;
  }
  const handleRemove = (slug: string) => {
    removeBookmark(slug);
    setBookmarks(getBookmarks());
  };
  return <div className="space-y-6" data-id="element-3405">
      <div className="flex items-center justify-between" data-id="element-3406">
        <h1 className="text-2xl font-bold text-light-1" data-id="element-3407">Anunțuri salvate</h1>
        <span className="text-xs text-light-3" data-id="element-3408">
          {bookmarks.length}{' '}
          {bookmarks.length === 1 ? 'anunț salvat' : 'anunțuri salvate'}
        </span>
      </div>

      <p className="text-sm text-light-3 max-w-2xl" data-id="element-3409">
        Salvează aici anunțurile chiriașilor pe care vrei să-i contactezi mai
        târziu — de exemplu, când termini de făcut pozele apartamentului.
      </p>

      {/* Empty state */}
      {bookmarks.length === 0 ? <div className="rounded-2xl bg-dark-2 border border-dark-3 p-10 sm:p-14 text-center" data-id="element-3410">
          <div className="w-14 h-14 rounded-2xl bg-dark-3 flex items-center justify-center mx-auto mb-5" data-id="element-3411">
            <BookmarkSimple size={28} weight="duotone" className="text-light-2" data-id="element-3412" />
          </div>
          <h3 className="text-lg font-semibold text-light-1 mb-2" data-id="element-3413">
            Niciun anunț salvat încă
          </h3>
          <p className="text-sm text-light-3 leading-relaxed max-w-md mx-auto mb-6" data-id="element-3414">
            Răsfoiește Marketplace-ul și apasă pe iconița de bookmark din colțul
            fiecărui anunț ca să-l salvezi aici.
          </p>
          <Button onClick={() => navigate(PAGE_ROUTE.MARKETPLACE)} className="bg-lime-1 hover:bg-lime-2 text-dark-1 font-semibold h-11 px-6" data-id="element-3415">
            <MagnifyingGlass size={18} weight="bold" className="mr-2" data-id="element-3416" />
            Explorează Marketplace
          </Button>
        </div> : <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3417">
          {bookmarks.map(b => <SavedProfileCard key={b.slug} profile={b} onOpen={() => navigate(PAGE_ROUTE.TENANT_ANUNT, {
        slug: b.slug
      })} onRemove={() => handleRemove(b.slug)} data-id="element-3418" />)}
        </div>}
    </div>;
}
function SavedProfileCard({
  profile,
  onOpen,
  onRemove
}: {
  profile: BookmarkedProfile;
  onOpen: () => void;
  onRemove: () => void;
}) {
  const isInactive = profile.status === 'inactive';
  return <div className={cn('rounded-2xl border transition-colors overflow-hidden flex flex-col', isInactive ? 'bg-dark-2/50 border-dark-4 opacity-75' : 'bg-dark-2 border-dark-3 hover:border-dark-4')} data-id="element-3419">
      <button onClick={onOpen} className="text-left p-5 flex items-start gap-4 hover:bg-dark-3/40 transition-colors cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-lime-1 rounded-t-2xl relative" data-id="element-3420">
        {isInactive && <div className="absolute top-3 right-3 bg-dark-4 text-light-3 text-[10px] font-medium px-2 py-0.5 rounded uppercase tracking-wider" data-id="element-3421">
            Anunț încheiat
          </div>}
        {profile.profilePhoto ? <img src={profile.profilePhoto} alt={`${profile.firstName} ${profile.lastName}`} className={cn('w-12 h-12 rounded-full object-cover shrink-0', isInactive && 'grayscale')} data-id="element-3422" /> : <div className={cn('w-12 h-12 rounded-full bg-dark-3 flex items-center justify-center text-base font-medium text-light-2 shrink-0', isInactive && 'grayscale')} data-id="element-3423">
            {profile.firstName[0]}
          </div>}
        <div className="min-w-0 flex-1" data-id="element-3424">
          <div className="flex items-center gap-1.5 pr-16" data-id="element-3425">
            <h3 className="text-base font-medium text-light-1 truncate" data-id="element-3426">
              {profile.firstName} {profile.lastName}
            </h3>
            {profile.isVerified && <SealCheck size={14} weight="fill" className={isInactive ? 'text-light-4' : 'text-lime-1'} data-id="element-3427" />}
          </div>
          <p className="text-xs text-light-3 truncate mt-0.5" data-id="element-3428">
            {profile.occupation}
          </p>
          <ul className="mt-3 space-y-1.5 text-xs text-light-2" data-id="element-3429">
            <li className="flex items-center gap-2" data-id="element-3430">
              <Wallet size={14} weight="regular" className="text-light-3" data-id="element-3431" />
              <span data-id="element-3432">{formatBudget(profile.budgetMax)}</span>
            </li>
            {profile.neighborhoods && <li className="flex items-center gap-2 min-w-0" data-id="element-3433">
                <MapPin size={14} weight="regular" className="text-light-3 shrink-0" data-id="element-3434" />
                <span className="truncate" data-id="element-3435">{profile.neighborhoods}</span>
              </li>}
          </ul>
        </div>
      </button>
      <div className="px-5 py-3 border-t border-dark-3 flex items-center justify-between gap-2" data-id="element-3436">
        <span className="text-[11px] text-light-4" data-id="element-3437">
          Salvat{' '}
          {new Date(profile.savedAt).toLocaleDateString('ro-RO', {
          day: 'numeric',
          month: 'short'
        })}
        </span>
        <div className="flex items-center gap-3" data-id="element-3438">
          {isInactive && <button onClick={onRemove} className="text-xs text-light-3 hover:text-light-1 inline-flex items-center gap-1.5 cursor-pointer transition-colors" data-id="element-3439">
              <Archive size={14} weight="regular" data-id="element-3440" />
              Arhivează
            </button>}
          <button onClick={onRemove} className="text-xs text-light-3 hover:text-red-1 inline-flex items-center gap-1.5 cursor-pointer transition-colors" aria-label="Elimină din salvate" data-id="element-3441">
            <Trash size={14} weight="regular" data-id="element-3442" />
            Șterge
          </button>
        </div>
      </div>
    </div>;
}